import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { CartContext } from "../context/CartContext";
import { createOrder } from "../api/orderService";
import { createPaymentOrder, verifyPayment } from "../api/paymentService";
import "./Checkout.css";

const Checkout = () => {
  const { cartItems, clearCart } = useContext(CartContext);

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    customerName: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });

  const [loading, setLoading] = useState(false);

  const getEffectivePrice = (item) => {
    if (
      item.salePrice !== null &&
      item.salePrice !== undefined &&
      Number(item.salePrice) < Number(item.price)
    ) {
      return Number(item.salePrice);
    }

    return Number(item.price);
  };

  const getCheckoutTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = getEffectivePrice(item);

      return total + price * Number(item.quantity);
    }, 0);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const name = formData.customerName.trim();
    const phone = formData.phone.trim();
    const email = formData.email.trim();
    const address = formData.address.trim();
    const city = formData.city.trim();
    const state = formData.state.trim();
    const pincode = formData.pincode.trim();

    if (name.length < 2) {
      alert("Please enter your full name.");
      return false;
    }

    if (!/^[0-9]{10}$/.test(phone)) {
      alert("Please enter a valid 10-digit phone number.");
      return false;
    }

    if (!email) {
      alert("Please enter your email address.");
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert("Please enter a valid email address.");
      return false;
    }

    if (address.length < 5) {
      alert("Please enter your complete address.");
      return false;
    }

    if (city.length < 2) {
      alert("Please enter your city.");
      return false;
    }

    if (state.length < 2) {
      alert("Please enter your state.");
      return false;
    }

    if (!/^[0-9]{6}$/.test(pincode)) {
      alert("Please enter a valid 6-digit pincode.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (cartItems.length === 0) {
      alert("Your cart is empty.");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      /*
       * STEP 1
       * Create the customer order in our database.
       */
      const orderData = {
        customerName: formData.customerName.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        pincode: formData.pincode.trim(),

        items: cartItems.map((item) => ({
          productId: item.id,
          quantity: Number(item.quantity),
        })),
      };

      console.log("Creating customer order:", orderData);

      const order = await createOrder(orderData);

      if (!order || !order.id) {
        throw new Error(
          "Customer order was created but no Order ID was returned.",
        );
      }

      console.log("Customer order created:", order);

      /*
       * STEP 2
       * Ask our backend to create a Razorpay order.
       */
      const paymentOrder = await createPaymentOrder(order.id);

      if (!paymentOrder || !paymentOrder.razorpayOrderId) {
        throw new Error("Unable to create Razorpay payment order.");
      }

      console.log("Razorpay order created:", paymentOrder);

      /*
       * STEP 3
       * Make sure Razorpay Checkout is loaded.
       */
      if (!window.Razorpay) {
        throw new Error(
          "Razorpay Checkout could not be loaded. Please refresh the page and try again.",
        );
      }

      /*
       * STEP 4
       * Configure Razorpay Checkout.
       */
      const options = {
        key: paymentOrder.keyId,

        amount: Math.round(Number(paymentOrder.amount) * 100),

        currency: paymentOrder.currency || "INR",

        name: "STAR GOLD",

        description: "Luxury Jewellery & Accessories",

        order_id: paymentOrder.razorpayOrderId,

        prefill: {
          name: formData.customerName.trim(),
          email: formData.email.trim(),
          contact: formData.phone.trim(),
        },

        notes: {
          customerOrderId: String(order.id),
        },

        theme: {
          color: "#c9a227",
        },

        handler: async function (response) {
          try {
            console.log("Razorpay payment successful:", response);

            /*
             * STEP 5
             * Send Razorpay payment information
             * to our Spring Boot backend.
             */
            const verificationData = {
              customerOrderId: order.id,

              razorpayOrderId: response.razorpay_order_id,

              razorpayPaymentId: response.razorpay_payment_id,

              razorpaySignature: response.razorpay_signature,
            };

            console.log("Verifying payment:", verificationData);

            await verifyPayment(verificationData);

            /*
             * STEP 6
             * Payment is verified successfully.
             *
             * ONLY NOW clear the cart.
             */
            clearCart();

            /*
             * STEP 7
             * Go to order success page.
             */
            navigate(`/order-success/${order.id}`);
          } catch (verificationError) {
            console.error("Payment verification failed:", verificationError);

            const backendMessage =
              typeof verificationError.response?.data === "string"
                ? verificationError.response.data
                : verificationError.response?.data?.message;

            alert(
              backendMessage ||
                "Payment was received, but verification failed. Please contact STAR GOLD with your Order ID: #" +
                  order.id,
            );

            setLoading(false);
          }
        },

        modal: {
          ondismiss: function () {
            console.log("Razorpay checkout closed.");

            setLoading(false);

            alert("Payment was cancelled. Your order has not been confirmed.");
          },
        },
      };

      /*
       * STEP 8
       * Open Razorpay Checkout.
       */
      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function (response) {
        console.error("Razorpay payment failed:", response);

        setLoading(false);

        const reason = response.error?.description || "Payment failed.";

        alert(reason + "\n\nYour Order ID is #" + order.id);
      });

      razorpay.open();
    } catch (error) {
      console.error("Checkout/payment initialization failed:", error);

      const backendMessage =
        typeof error.response?.data === "string"
          ? error.response.data
          : error.response?.data?.message;

      const message =
        backendMessage ||
        error.message ||
        "Unable to start payment. Please try again.";

      alert(message);

      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <main className="checkout-page">
        <div className="checkout-empty">
          <p className="checkout-brand">STAR GOLD</p>

          <h1>Your Cart Is Empty</h1>

          <p>Add some jewellery to your cart before checking out.</p>

          <Link to="/collections">Continue Shopping</Link>
        </div>
      </main>
    );
  }

  const checkoutTotal = getCheckoutTotal();

  return (
    <main className="checkout-page">
      <div className="checkout-container">
        {/* HEADER */}

        <div className="checkout-header">
          <p>STAR GOLD</p>

          <h1>Checkout</h1>

          <span>Complete your order details</span>
        </div>

        <div className="checkout-layout">
          {/* CUSTOMER FORM */}

          <section className="checkout-form-section">
            <h2>Customer Details</h2>

            <form onSubmit={handleSubmit}>
              <div className="checkout-field">
                <label htmlFor="customerName">Full Name</label>

                <input
                  id="customerName"
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="phone">Phone Number</label>

                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  maxLength="10"
                  inputMode="numeric"
                  autoComplete="tel"
                  required
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="email">Email</label>

                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="address">Delivery Address</label>

                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="House / Flat number, street, area..."
                  rows="4"
                  autoComplete="street-address"
                  required
                />
              </div>

              <div className="checkout-two-columns">
                <div className="checkout-field">
                  <label htmlFor="city">City</label>

                  <input
                    id="city"
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    autoComplete="address-level2"
                    required
                  />
                </div>

                <div className="checkout-field">
                  <label htmlFor="state">State</label>

                  <input
                    id="state"
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                    autoComplete="address-level1"
                    required
                  />
                </div>
              </div>

              <div className="checkout-field">
                <label htmlFor="pincode">Pincode</label>

                <input
                  id="pincode"
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  placeholder="6-digit pincode"
                  maxLength="6"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  required
                />
              </div>

              {/* PAYMENT INFORMATION */}

              <div className="checkout-payment-note">
                <strong>Secure Online Payment</strong>

                <p>
                  You will be securely redirected to Razorpay after submitting
                  your order. Your payment details are handled securely by
                  Razorpay.
                </p>
              </div>

              <button
                type="submit"
                className="checkout-pay-button"
                disabled={loading}
              >
                {loading ? "Preparing Payment..." : "Pay Securely"}
              </button>
            </form>
          </section>

          {/* ORDER SUMMARY */}

          <aside className="checkout-summary">
            <h2>Order Summary</h2>

            <div className="checkout-items">
              {cartItems.map((item) => {
                const price = getEffectivePrice(item);

                const originalPrice = Number(item.price);

                const hasDiscount =
                  item.salePrice !== null &&
                  item.salePrice !== undefined &&
                  Number(item.salePrice) < originalPrice;

                return (
                  <div className="checkout-item" key={item.id}>
                    <div className="checkout-item-image">
                      <img src={item.imageUrl} alt={item.name} />
                    </div>

                    <div className="checkout-item-info">
                      <strong>{item.name}</strong>

                      <span>Qty: {item.quantity}</span>

                      <div className="checkout-item-price">
                        <strong>₹{price.toLocaleString("en-IN")}</strong>

                        {hasDiscount && (
                          <span>₹{originalPrice.toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="checkout-total">
              <span>Total</span>

              <strong>₹{checkoutTotal.toLocaleString("en-IN")}</strong>
            </div>

            <div className="checkout-security">
              <span>✓</span>

              <p>Order securely with STAR GOLD</p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default Checkout;
